import { Link, useRouterState } from "@tanstack/react-router";
import { Handshake } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useIssuers } from "@modules/billing/api/issuers";
import { useAuthStore } from "@shared/store/auth";
import { useCommercialStore } from "../store";

const BASE_TABS = [
  { label: "Resumen", to: "/commercial", permission: "view-commercial" },
  { label: "Valoraciones", to: "/commercial/valuations", permission: "view-commercial" },
  { label: "Costes", to: "/commercial/costs", permission: "manage-commercial-costs" },
  { label: "Beneficios", to: "/commercial/benefits", permission: "view-commercial-benefits" },
  { label: "Configuración", to: "/commercial/settings", permission: "manage-commercial-settings" },
] as const;

export function CommercialHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  const { data: emisores } = useIssuers();
  const { emisorId, setEmisorId } = useCommercialStore();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const permissions = useAuthStore((s) => s.me?.permissions ?? []);

  useEffect(() => {
    if (emisorId === null && emisores?.length) setEmisorId(emisores[0].id);
  }, [emisores, emisorId, setEmisorId]);

  const tabs = useMemo(
    () => BASE_TABS.filter((tab) => permissions.includes(tab.permission)),
    [permissions],
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div className="flex items-start gap-4">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-emerald-50 text-emerald-700">
            <Handshake size={24} />
          </div>
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">Comercial</p>
            <h1 className="mt-1 text-3xl font-black tracking-tight text-slate-900">{title}</h1>
            {subtitle && <p className="mt-1 text-sm font-semibold text-slate-500">{subtitle}</p>}
          </div>
        </div>

        <label className="min-w-[280px] text-xs font-black uppercase tracking-wide text-slate-500">
          Empresa
          <select
            value={emisorId ?? ""}
            onChange={(e) => setEmisorId(Number(e.target.value) || null)}
            className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-800 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15"
          >
            <option value="">Selecciona empresa</option>
            {emisores?.map((emisor) => (
              <option key={emisor.id} value={emisor.id}>{emisor.nombre}</option>
            ))}
          </select>
        </label>
      </div>

      <nav className="flex flex-wrap gap-1 border-b border-slate-200">
        {tabs.map((tab) => {
          const active = tab.to === "/commercial" ? pathname === tab.to : pathname.startsWith(tab.to);
          return (
            <Link
              key={tab.to}
              to={tab.to}
              className={[
                "-mb-px rounded-t-lg border-b-2 px-4 py-2.5 text-sm font-bold transition",
                active ? "border-emerald-700 bg-white text-emerald-800" : "border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-800",
              ].join(" ")}
            >
              {tab.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
